import React from 'react';

/* ─── Shimmer keyframes injected once ─── */
const SHIMMER_ID = 'cxo-shimmer-style';
if (typeof document !== 'undefined' && !document.getElementById(SHIMMER_ID)) {
  const style = document.createElement('style');
  style.id = SHIMMER_ID;
  style.textContent = `
    @keyframes cxoShimmer {
      0% { background-position: -400px 0; }
      100% { background-position: 400px 0; }
    }
  `;
  document.head.appendChild(style);
}

const shimmerBg = {
  background: 'linear-gradient(90deg, rgba(255,255,255,0.03) 25%, rgba(255,255,255,0.06) 50%, rgba(255,255,255,0.03) 75%)',
  backgroundSize: '400px 100%',
  animation: 'cxoShimmer 1.6s ease-in-out infinite',
};

/* ─── Base block ─── */
export function SkeletonBlock({ width = '100%', height = 16, radius = 6, style = {} }) {
  return <div style={{ width, height, borderRadius: radius, ...shimmerBg, ...style }} />;
}

/* ─── Full-page loader (for lazy-loaded pages) ─── */
export function PageLoader() {
  return (
    <div style={{ minHeight: '100vh', background: '#050d1a', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
      <div style={{ fontSize: 24, fontWeight: 700 }}>
        <span style={{ color: '#C9A96E' }}>CXO</span>
        <span style={{ color: '#fff', fontWeight: 300, fontStyle: 'italic' }}>Era</span>
      </div>
      <div style={{ width: 36, height: 36, border: '2px solid rgba(255,255,255,0.06)', borderTopColor: '#C9A96E', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>
  );
}

/* ─── Admin table skeleton ─── */
export function AdminTableSkeleton({ rows = 5, cols = 4 }) {
  return (
    <div style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 32 }}>
        {[...Array(cols)].map((_, i) => (
          <SkeletonBlock key={i} width={80 + Math.random() * 60} height={10} />
        ))}
      </div>
      {/* Rows */}
      {[...Array(rows)].map((_, i) => (
        <div key={i} style={{ padding: '16px 24px', borderBottom: '1px solid rgba(255,255,255,0.03)', display: 'flex', gap: 32, alignItems: 'center' }}>
          {[...Array(cols)].map((_, j) => (
            <SkeletonBlock key={j} width={60 + Math.random() * 80} height={14} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─── Admin card grid skeleton (for Talks, Speakers) ─── */
export function AdminCardSkeleton({ count = 4 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))', gap: 20 }}>
      {[...Array(count)].map((_, i) => (
        <div key={i} style={{ background: '#0A1628', border: '1px solid rgba(201,169,110,0.1)', padding: 24 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <SkeletonBlock width={70} height={20} radius={4} />
            <SkeletonBlock width={90} height={20} radius={4} />
          </div>
          <SkeletonBlock width="80%" height={18} style={{ marginBottom: 10 }} />
          <SkeletonBlock width={100} height={12} style={{ marginBottom: 16 }} />
          <SkeletonBlock width="100%" height={40} radius={4} style={{ marginBottom: 16 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <SkeletonBlock width={50} height={16} radius={4} />
            <SkeletonBlock width={60} height={16} radius={4} />
            <SkeletonBlock width={45} height={16} radius={4} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Filter row skeleton ─── */
export function FilterRowSkeleton({ count = 4 }) {
  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
      {[...Array(count)].map((_, i) => (
        <SkeletonBlock key={i} width={90 + Math.random() * 30} height={36} radius={4} />
      ))}
    </div>
  );
}

/* ─── Stats cards row skeleton (for RegistrationsPanel) ─── */
export function StatsCardsSkeleton({ count = 4 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${count}, 1fr)`, gap: 16, marginBottom: 24 }}>
      {[...Array(count)].map((_, i) => (
        <div key={i} style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: '20px 16px' }}>
          <SkeletonBlock width={40} height={28} style={{ marginBottom: 8 }} />
          <SkeletonBlock width={80} height={10} />
        </div>
      ))}
    </div>
  );
}

/* ─── Full admin panel skeleton (filter + table) ─── */
export function AdminPanelSkeleton({ type = 'table', filterCount = 4 }) {
  return (
    <div>
      <FilterRowSkeleton count={filterCount} />
      {type === 'table' ? <AdminTableSkeleton /> : <AdminCardSkeleton />}
    </div>
  );
}

/* ─── Hero section skeleton (for HomePage) ─── */
export function HeroSkeleton() {
  return (
    <div style={{ minHeight: '100vh', padding: '160px 60px 80px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <SkeletonBlock width={180} height={10} style={{ marginBottom: 24 }} />
      <SkeletonBlock width="60%" height={60} style={{ marginBottom: 20 }} />
      <SkeletonBlock width="50%" height={50} style={{ marginBottom: 32 }} />
      <SkeletonBlock width="40%" height={16} style={{ marginBottom: 12 }} />
      <SkeletonBlock width="35%" height={16} style={{ marginBottom: 48 }} />
      <div style={{ display: 'flex', gap: 16 }}>
        <SkeletonBlock width={160} height={48} radius={4} />
        <SkeletonBlock width={140} height={48} radius={4} />
      </div>
    </div>
  );
}

/* ─── Program / Event card grid skeleton ─── */
export function CardGridSkeleton({ count = 6, minWidth = 280 }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`, gap: 24, padding: '40px 60px' }}>
      {[...Array(count)].map((_, i) => (
        <div key={i} style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, overflow: 'hidden' }}>
          <SkeletonBlock width="100%" height={180} radius={0} />
          <div style={{ padding: 20 }}>
            <SkeletonBlock width="70%" height={18} style={{ marginBottom: 10 }} />
            <SkeletonBlock width="90%" height={12} style={{ marginBottom: 8 }} />
            <SkeletonBlock width="60%" height={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ─── Event detail skeleton ─── */
export function EventDetailSkeleton() {
  return (
    <div style={{ padding: '100px 60px 60px' }}>
      <SkeletonBlock width={120} height={10} style={{ marginBottom: 16 }} />
      <SkeletonBlock width="50%" height={36} style={{ marginBottom: 12 }} />
      <SkeletonBlock width="30%" height={14} style={{ marginBottom: 32 }} />
      <SkeletonBlock width="100%" height={300} radius={12} style={{ marginBottom: 32 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <SkeletonBlock width="100%" height={200} radius={8} />
        <SkeletonBlock width="100%" height={200} radius={8} />
      </div>
    </div>
  );
}
